import { Epic } from 'redux-observable';
import { Observable } from 'rxjs';

import { Action } from '../actions';
import { resetGridData } from '../actions/grid-screen';
import {
    PRICES_WIZARD_SAVE_PRICES,
    PricesWizardGeneralError,
    pricesWizardGeneralError,
    PricesWizardPricesSaved,
    pricesWizardPricesSaved
} from '../actions/grid-wizards';
import {
    mapPricesMasterDataToAutoBidsInputApiData,
    mapPricesMasterDataToBidsInputApiData
} from '../mappers/phrase/phrase';
import { selectGridSourceParams } from '../selectors/gridSourceParams/gridSourceParams';
import { NotSyncedError } from '../service/Errors';
import { getPricesWizardSavedSuccessMessage } from '../service/gridItemsActionsHelpers/gridItemsActionsHelpers';
import { fireMetrika } from '../service/Metrika/metrika';
import { showRequestResultNoticeMessage } from '../service/NoticeTypeHelper/NoticeTypeHelper';
import { PricesWizardHelpers } from '../service/PricesWizardHelpers/PricesWizardHelpers';
import '../service/Rx';
import { EpicDeps } from '../service/StoreFactory';
import { setAutoBidsMutation, setBidsMutation } from '../typings/graphql-grid';
import { PricesWizardType } from '../typings/inspector';
import { State } from '../typings/state';

const acceptPricesWizardChanges: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.ofType(PRICES_WIZARD_SAVE_PRICES).mergeMap(
        (): Observable<Action> => {
            const state = store$.value;
            const sourceParams = selectGridSourceParams(state);
            const {
                selectionInfo: { phrases: phrasesIds },
                hasObjectsOverLimit,
                gridData: { total }
            } = state.grid;
            const { length: phrasesCount } = phrasesIds;

            const data = PricesWizardHelpers.mapPricesWizardStateToExportData(
                state.grid.pricesWizardData
            );

            let responsePromise: Promise<setBidsMutation | setAutoBidsMutation>;

            if (data.wizardType === PricesWizardType.simple) {
                responsePromise = deps.pricesWizardMutationExecutor.setBids(
                    mapPricesMasterDataToBidsInputApiData(data, phrasesIds),
                    sourceParams.login
                );
            } else {
                responsePromise = deps.pricesWizardMutationExecutor.setAutoBids(
                    mapPricesMasterDataToAutoBidsInputApiData(data, phrasesIds),
                    sourceParams.login
                );
            }

            const response$ = Observable.fromPromise(responsePromise);

            return response$
                .map(response => {
                    // TODO: кажется, showNoticeMessage надо куда-то убирать из actions/grid-screen
                    const updatedKeywords =
                        data.wizardType === PricesWizardType.simple
                            ? (response as setBidsMutation).setBids
                                  .showConditionIds
                            : (response as setAutoBidsMutation).setAutoBids
                                  .keywordIds;
                    const setCount = (updatedKeywords || []).length;
                    const needObjectsOverLimitNotification =
                        total === phrasesCount && hasObjectsOverLimit;
                    const successMessage = getPricesWizardSavedSuccessMessage({
                        phrasesCount: phrasesCount,
                        setCount,
                        needObjectsOverLimitNotification
                    });

                    showRequestResultNoticeMessage(
                        phrasesCount,
                        setCount,
                        successMessage
                    );

                    fireMetrika({
                        pricesWizard: {
                            saved: data
                        }
                    });
                })
                .mapTo(pricesWizardPricesSaved())
                .merge(response$.mapTo(resetGridData()))
                .catch(
                    (
                        e
                    ): Observable<
                        PricesWizardGeneralError | PricesWizardPricesSaved
                    > => {
                        if (e instanceof NotSyncedError) {
                            return Observable.of(pricesWizardPricesSaved());
                        } else {
                            return Observable.of(
                                pricesWizardGeneralError(e.getMessage())
                            );
                        }
                    }
                );
        }
    );
};

export { acceptPricesWizardChanges };
